/**
 * Lab leave-guard — MVP PR A3.
 *
 * Cartridge `LeaveStarterSceneTrigger` (3 entries at row 8 cols 5/6/7,
 * gated on `VAR_MAP_SCENE_PALLET_TOWN_PROFESSOR_OAKS_LAB == 2`). Fires
 * when the player tries to leave the lab BEFORE picking a starter.
 * Cartridge text: "OAK: Hey! Don't go away yet!" — then `applymovement`
 * walks the player back north 1 tile so they're off the trigger row.
 *
 * Beat structure:
 *
 *     ┌────────────────────────────────────────────────────────┐
 *     │  trigger fires at (5/6/7, 8) when var=2                 │
 *     │            │                                            │
 *     │            ▼                                            │
 *     │  showDialogue("OAK: Hey! Don't go away yet!")           │
 *     │            │                                            │
 *     │            ▼                                            │
 *     │  walkPlayerAlongPath: north 1 tile (back to row 7)      │
 *     │            │                                            │
 *     │            ▼                                            │
 *     │  state stays at 2 — player must pick a ball             │
 *     └────────────────────────────────────────────────────────┘
 *
 * State stays at 2 throughout. No flag-set; the trigger is naturally
 * re-eligible if the player walks south again (cartridge same).
 */
import type { Handler, ScriptCtx } from '../scriptRunner';
import { effectsReduced } from '../../render/motion';

const OAK_DONT_GO_AWAY = [
  'OAK: Hey!',
  "Don't go away yet!",
];

export const labLeaveGuardHandler: Handler = async (ctx: ScriptCtx) => {
  await ctx.worldScene.runHandlerBracket(async () => {
    const walkSpeed = effectsReduced() ? 192 : 96;
    const player = ctx.worldScene.getPlayerTile();
    ctx.worldScene.showDialogue(OAK_DONT_GO_AWAY, null);
    await ctx.worldScene.waitForDialogueClose();
    // Walk player back north 1 tile so they're off the trigger row.
    // Cartridge bounces them via applymovement; same effect.
    await ctx.worldScene.walkPlayerAlongPath({
      waypoints: [{ x: player.x, y: player.y - 1 }],
      pixelsPerSecond: walkSpeed,
    });
  });
};

/** Trigger refs registered for the leave-guard. All three columns
 *  route to the same handler. */
export const LAB_LEAVE_GUARD_TRIGGER_REFS: readonly string[] = [
  'PalletTown_ProfessorOaksLab_EventScript_LeaveStarterSceneTrigger',
];
